import {Checkbox, Field, Label} from "@zendeskgarden/react-forms";
import {Spinner} from "@zendeskgarden/react-loaders";
import {ReactElement} from "react";
import {toast} from "react-toastify";
import {
    ClientFeatureEnum,
    useGetClientFeaturesQuery,
    useRemoveClientFeatureMutation,
    useSetClientFeatureMutation
} from "../../../__generated__/generated_types";
import FeatureRestrictedOverlay from "../../../components/feature-restricted-overlay/FeatureRestrictedOverlay";
import {AdminWrapper} from "../AdminWrapper";

export function ClientFeatures(): ReactElement {
    const {data, loading, refetch} = useGetClientFeaturesQuery({
        fetchPolicy: "no-cache"
    });
    const [setClientFeature, {loading: setClientFeatureLoading}] = useSetClientFeatureMutation();
    const [removeClientFeature, {loading: removeClientFeatureLoading}] = useRemoveClientFeatureMutation();

    const isLoading = loading || setClientFeatureLoading || removeClientFeatureLoading;
    const allowManualMarketRent = data?.getClientFeatures.allowManualMarketRent ?? false;
    const disallowAcquisitionProperties = data?.getClientFeatures.disallowAcquisitionProperties ?? false;
    const useOldOperationalMetricsUI = data?.getClientFeatures.useOldOperationalMetricsUI ?? false;
    const hideRevenueWizard = data?.getClientFeatures.hideRevenueWizard ?? false;
    const adminModelDownEnabled = data?.getClientFeatures.adminModelDownEnabled ?? false;
    const pushingPayrollDefaults = data?.getClientFeatures.pushingPayrollDefaults ?? false;
    const showLossLeaders = data?.getClientFeatures.showLossLeaders ?? false;
    const showWorkflows = data?.getClientFeatures.showWorkflows ?? false;
    const userSpecifiedUnitCount = data?.getClientFeatures.userSpecifiedUnitCounts ?? false;
    const restrictExecutiveSummaryAccess = data?.getClientFeatures.restrictExecutiveSummaryAccess ?? false;
    const enableInitialGprSetup = data?.getClientFeatures.enableInitialGprSetup ?? false;
    const sideBySideComparison = data?.getClientFeatures.sideBySideComparison ?? false;
    const executiveSummaryCustomNarrative = data?.getClientFeatures.executiveSummaryCustomNarrative ?? false;

    function onFeatureChange(feature: ClientFeatureEnum, checked: boolean) {
        if(checked) {
            setClientFeature({
                variables: {
                    feature: feature
                }
            })
                .then(() => refetch())
                .then(() => {
                    toast.success("Client feature enabled");
                })
                .catch(() => {
                    toast.error("Failed to enable client feature");
                });
        } else {
            removeClientFeature({
                variables: {
                    feature: feature
                }
            })
                .then(() => refetch())
                .then(() => {
                    toast.success("Client feature removed");
                })
                .catch(() => {
                    toast.error("Failed to remove client feature");
                });
        }
    }

    return <AdminWrapper>
        <FeatureRestrictedOverlay coverPage />
        <div style={{display: "grid", gap: "16px", maxWidth: "720px"}}>
            <div>
                <h3>Client Features</h3>
                <p>Enable or remove internal client-level feature flags.</p>
            </div>

            {loading && !data ? (
                <Spinner size="32" />
            ) : (
                <>
                    <Field>
                        <Checkbox
                            checked={allowManualMarketRent}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.ManualMarketRent, event.target.checked)}
                        >
                            <Label>Manual Market Rent</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={useOldOperationalMetricsUI}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.UseOldOperationalMetricsUi, event.target.checked)}
                        >
                            <Label>Show Old Operational Revenue Metric Screens</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={hideRevenueWizard}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.HideRevenueWizard, event.target.checked)}
                        >
                            <Label>Hide Revenue Wizard</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={disallowAcquisitionProperties}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.DisallowAcquisitionProperties, event.target.checked)}
                        >
                            <Label>Budget Builder Disabled</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={adminModelDownEnabled}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.AdminModelDownEnabled, event.target.checked)}
                        >
                            <Label>Admin Model Down Enabled</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={pushingPayrollDefaults}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.PushingPayrollDefaults, event.target.checked)}
                        >
                            <Label>Pushing Payroll Defaults</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={showWorkflows}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.ShowWorkflows, event.target.checked)}
                        >
                            <Label>Show Workflows for Non-Internal Users</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={showLossLeaders}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.ShowLossLeaders, event.target.checked)}
                        >
                            <Label>Move-In Discount</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={userSpecifiedUnitCount}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.ShowUserSpecifiedUnitCounts, event.target.checked)}
                        >
                            <Label>Unit Count Adjustment</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={restrictExecutiveSummaryAccess}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.RestrictExecutiveSummaryAccess, event.target.checked)}
                        >
                            <Label>Restrict Executive Summary Share/Approve Access</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={enableInitialGprSetup}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.EnableInitialGprSetup, event.target.checked)}
                        >
                            <Label>Enable Initial GPR Setup</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={sideBySideComparison}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.SideBySideComparison, event.target.checked)}
                        >
                            <Label>Side-by-Side Portfolio Comparison</Label>
                        </Checkbox>
                    </Field>
                    <Field>
                        <Checkbox
                            checked={executiveSummaryCustomNarrative}
                            disabled={isLoading}
                            onChange={(event) => onFeatureChange(ClientFeatureEnum.ExecutiveSummaryCustomNarrative, event.target.checked)}
                        >
                            <Label>Enable Executive Summary Custom Narrative</Label>
                        </Checkbox>
                    </Field>
                </>
            )}
        </div>
    </AdminWrapper>;
}
